'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Container } from '@mantine/core';
import styles from './GeneralConceptPresentation.module.scss';
import PresentationOfSection from '../Atoms/PresentationOfSection/PresentationOfSection';
import { useAOS } from '@/src/lib/hooks/useAOS';

export type GCPImage = {
    src: string;
    alt: string;
};

export type GCPStoryStep = {
    kicker?: string;   // "1 • On choisit"
    title: string;     // short title
    text: string;      // 1 sentence
    badge?: string;    // "🎯 Mission du jour"
};

export type GeneralConceptPresentationSlice = {
    type: 'general-concept-presentation';

    tone?: 'kids' | 'serious';
    showSeparator?: boolean;

    title?: string;
    subtitle?: string;

    images: {
        big: GCPImage;
        small1: GCPImage;
        small2: GCPImage;
    };

    hook: string;

    // Story mode
    storySteps?: GCPStoryStep[];

    cta?: {
        label: string;
        href: string;
    };

    aos?: {
        animation?: string;
        delay?: number;
    };
};

export default function GeneralConceptPresentation({ data }: { data: GeneralConceptPresentationSlice }) {
    useAOS();

    const {
        tone = 'kids',
        showSeparator = true,
        title,
        subtitle,
        images,
        hook,
        storySteps = [],
        cta,
        aos,
    } = data;

    const safeSteps = (storySteps ?? []).slice(0, 3);

    return (
        <section
            className={[
                styles.wrapper,
                tone === 'kids' ? styles.kids : styles.serious,
                showSeparator ? styles.withSeparator : styles.noSeparator,
            ].join(' ')}
            data-aos={aos?.animation ?? 'fade-up'}
            data-aos-delay={typeof aos?.delay === 'number' ? aos.delay : undefined}
            aria-label="General concept presentation"
        >
            <Container size="xl" className={styles.container}>
                <PresentationOfSection
                    title={title}
                    description={subtitle}
                    align="center"
                    aos={{ animation: 'fade-up', delay: 0 }}
                />

                <div className={styles.grid}>
                    {/* LEFT: IMAGES (kept like original) */}
                    <div className={styles.visual} aria-label="Concept visuals">
                        <div className={styles.big} data-aos="fade" data-aos-delay="60">
                            <Image
                                src={images.big.src}
                                alt={images.big.alt}
                                fill
                                sizes="(max-width: 980px) 100vw, 62vw"
                                className={styles.image}
                                priority
                            />
                            <span className={styles.sheen} aria-hidden="true" />
                            <span className={styles.cornerStamp} aria-hidden="true" />
                        </div>

                        <div className={styles.smallStack}>
                            <div className={styles.smallOne} data-aos="fade-up" data-aos-delay="120">
                                <Image
                                    src={images.small1.src}
                                    alt={images.small1.alt}
                                    fill
                                    sizes="(max-width: 980px) 50vw, 18vw"
                                    className={styles.image}
                                    loading="lazy"
                                />
                                <span className={styles.sheen} aria-hidden="true" />
                            </div>

                            <div className={styles.smallTwo} data-aos="fade-up" data-aos-delay="180">
                                <Image
                                    src={images.small2.src}
                                    alt={images.small2.alt}
                                    fill
                                    sizes="(max-width: 980px) 50vw, 18vw"
                                    className={styles.image}
                                    loading="lazy"
                                />
                                <span className={styles.sheen} aria-hidden="true" />
                            </div>
                        </div>
                    </div>

                    {/* RIGHT: HOOK + ASYMMETRIC STORY */}
                    <div className={styles.text} data-aos="fade-up" data-aos-delay="120">
                        <p className={styles.hook}>{hook}</p>

                        {!!safeSteps.length && (
                            <div className={styles.story} aria-label="Mini histoire">
                                {safeSteps.map((s, i) => (
                                    <article
                                        key={`${s.title}-${i}`}
                                        className={styles.step}
                                        data-pos={i === 0 ? 'topRight' : i === 1 ? 'midLeft' : 'botRight'}
                                        data-aos="zoom-in"
                                        data-aos-delay={i * 800}
                                    >
                                        {s.badge && (
                                            <span className={styles.badge} aria-hidden="true">
                                                {s.badge}
                                            </span>
                                        )}

                                        {s.kicker && <p className={styles.kicker}>{s.kicker}</p>}
                                        <h3 className={styles.stepTitle}>{s.title}</h3>
                                        <p className={styles.stepText}>{s.text}</p>

                                        <span className={styles.cornerSparkle} aria-hidden="true" />
                                    </article>
                                ))}
                            </div>
                        )}

                        {cta && (
                            <Link href={cta.href} className={styles.cta} data-aos="fade-up" data-aos-delay="240">
                                {cta.label} <span aria-hidden="true">→</span>
                            </Link>
                        )}
                    </div>
                </div>
            </Container>
        </section>
    );
}
